import PropTypes from 'prop-types';
import React from 'react';
import { Tag, Tooltip } from 'antd';
import {
  CalendarOutlined,
  CheckOutlined,
  WarningOutlined,
  FormOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { FormattedMessage } from 'react-intl';
import {
  getChangeAccessMessageIntl,
  getHasNotFinishedPublishing,
} from '../../utils/PlusPublishingUtils';

const getEpisodeStatusConfig = (episode, availableTiers) => {
  const { status, encoding: { status: encodingStatus } = {} } = episode;

  const hasNotFinishedPublishing = availableTiers?.length
    ? getHasNotFinishedPublishing(episode)
    : false;
  const unfinishedPublishingTooltip =
    availableTiers && hasNotFinishedPublishing
      ? getChangeAccessMessageIntl(availableTiers, status, episode)?.hoverMessage
      : null;

  const configs = [
    {
      condition: encodingStatus === 'failed' || encodingStatus === 'error',
      icon: <WarningOutlined />,
      className: 'status-tag--error',
      message: <FormattedMessage defaultMessage="Encoding" />,
      tooltip: (
        <FormattedMessage defaultMessage="An error occurred while encoding your audio file. Try re-uploading the audio file." />
      ),
    },
    {
      condition:
        hasNotFinishedPublishing &&
        (encodingStatus === 'queued' || encodingStatus === 'processing'),
      icon: <SyncOutlined spin />,
      className: 'status-tag--default',
      message: <FormattedMessage defaultMessage="Encoding*" />,
      tooltip: unfinishedPublishingTooltip,
    },
    {
      condition: hasNotFinishedPublishing,
      icon: <SyncOutlined spin />,
      className: 'status-tag--default',
      message: <FormattedMessage defaultMessage="Published*" />,
      tooltip: unfinishedPublishingTooltip,
    },
    {
      condition:
        status === 'published' && (encodingStatus === 'queued' || encodingStatus === 'processing'),
      icon: <SyncOutlined spin />,
      className: 'status-tag--default',
      message: <FormattedMessage defaultMessage="Encoding" />,
      tooltip: (
        <div style={{ textAlign: 'center' }}>
          <FormattedMessage defaultMessage="Audio is encoding." />
          <br />
          <FormattedMessage defaultMessage='Episode status is set to "Published"' />
        </div>
      ),
    },
    {
      condition: status === 'published',
      icon: <CheckOutlined />,
      className: 'status-tag--success',
      message: <FormattedMessage defaultMessage="Published" />,
      tooltip: <FormattedMessage defaultMessage="Published" />,
    },
    {
      condition:
        status === 'scheduled' && (encodingStatus === 'queued' || encodingStatus === 'processing'),
      icon: <SyncOutlined spin />,
      className: 'status-tag--default',
      message: <FormattedMessage defaultMessage="Scheduled" />,
      tooltip: <FormattedMessage defaultMessage="Audio is encoding" />,
    },
    {
      condition: status === 'scheduled',
      icon: <CalendarOutlined />,
      className: 'status-tag--default',
      message: <FormattedMessage defaultMessage="Scheduled" />,
      tooltip: <FormattedMessage defaultMessage="Scheduled" />,
    },
    {
      condition:
        status === 'draft' && (encodingStatus === 'queued' || encodingStatus === 'processing'),
      icon: <SyncOutlined spin />,
      className: 'status-tag--default',
      message: <FormattedMessage defaultMessage="Draft" />,
      tooltip: <FormattedMessage defaultMessage="Audio is encoding" />,
    },
    {
      condition: status === 'draft',
      icon: <FormOutlined />,
      className: 'status-tag--default',
      message: <FormattedMessage defaultMessage="Draft" />,
      tooltip: <FormattedMessage defaultMessage="Draft" />,
    },
    {
      condition: status === 'importing',
      icon: <SyncOutlined spin />,
      className: 'status-tag--default',
      message: <FormattedMessage defaultMessage="Importing" />,
      tooltip: <FormattedMessage defaultMessage="Importing" />,
    },
    {
      condition: status === 'failed',
      icon: <WarningOutlined />,
      className: 'status-tag--error',
      message: <FormattedMessage defaultMessage="Failed" />,
      tooltip: <FormattedMessage defaultMessage="Failed" />,
    },
  ];

  return configs.find((item) => item.condition);
};

const EpisodeStatusTag = ({ episode, availableTiers }) => {
  const match = getEpisodeStatusConfig(episode, availableTiers);

  if (!match) {
    return null;
  }

  const { icon, className, message, tooltip } = match;

  return (
    <Tooltip title={tooltip}>
      <Tag className={`status-tag ${className}`}>
        <div>
          {icon}
          {message}
        </div>
      </Tag>
    </Tooltip>
  );
};

EpisodeStatusTag.propTypes = {
  episode: PropTypes.shape({
    status: PropTypes.oneOf(['failed', 'importing', 'scheduled', 'draft', 'published']),
    publishSequence: PropTypes.arrayOf(PropTypes.object),
    encoding: PropTypes.shape({
      status: PropTypes.oneOf(['failed', 'error', 'done', 'queued', 'processing']),
    }),
  }).isRequired,
  availableTiers: PropTypes.arrayOf(PropTypes.object),
};

export default EpisodeStatusTag;
